import { useRef, useEffect } from "react";
import type { SmartStreamStore } from "../SmartStreamStore";
import { ComposerManager } from "./ComposerManager";
import { defaultFormatSubmit } from "./formatContent";
import type { ComposerConfig, ComposerContent } from "./types";

/**
 * Create a reusable `useComposer` hook with app-wide attachment config.
 *
 * Call once at module level to configure uploads/validation, then use
 * the returned hook in any chat component by passing the stream store.
 *
 * @example
 * ```tsx
 * // Configure once (module level)
 * const useComposer = createComposer({
 *   attachments: {
 *     upload: (file) => uploadsApi.create({ file }),
 *     validate: (file) => file.size > 100_000_000 ? "Too large" : null,
 *   },
 * });
 *
 * // Use in any chat component
 * function ChatInput() {
 *   const store = useLanggraphStream({ apiUrl, assistantId, threadId });
 *   const composer = useComposer(store);
 *
 *   return (
 *     <ComposerProvider composer={composer}>
 *       <Textarea />
 *       <button onClick={() => composer.sendMessage()}>Send</button>
 *     </ComposerProvider>
 *   );
 * }
 * ```
 */
export function createComposer<TMeta extends Record<string, unknown> = Record<string, unknown>>(
  config: ComposerConfig<TMeta>,
) {
  const formatSubmit = config.formatSubmit ?? defaultFormatSubmit;

  return function useComposer(
    store: SmartStreamStore<any, any>,
  ): ComposerManager<TMeta> {
    // Keep store ref current so sendMessage always submits to the active stream
    const storeRef = useRef(store);
    storeRef.current = store;

    const managerRef = useRef<ComposerManager<TMeta> | null>(null);
    if (!managerRef.current) {
      managerRef.current = new ComposerManager<TMeta>(
        (content: ComposerContent) => {
          const { submit } = storeRef.current.getSnapshot();
          submit(formatSubmit(content));
        },
        config.attachments,
      );
    }

    // Cleanup preview URLs on unmount
    useEffect(() => {
      return () => {
        managerRef.current?.clear();
      };
    }, []);

    return managerRef.current;
  };
}
